import { Action } from '@/_core/fluxstore/types';
import orderBy from 'lodash/orderBy';
import { StorymapState, Subtask, Task, ViewType } from '../../types';
import * as smUse from '../../use';
import { useStorymapStore } from '../../useModule';
import { hasFocusedRelease } from '../../use/useFocusRelease';
import { isHideUnfilteredActive, matchingSubtaskIdSet, visibleTaskIdSet } from '../../use/useHideUnfiltered';
import { getFirstTrackingVisibleCard, getTrackingVisibleStatusColumns, getTrackingVisibleCardPosition } from '../../use/useTrackingVisibleCards';

const { registerReducer } = useStorymapStore();

export class SelectionMoveUp extends Action<StorymapState> {
    public static ACTION_TYPE = 'SelectionMoveUp';

    public constructor() {
        super(SelectionMoveUp.ACTION_TYPE);
    }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const reducer = (state: StorymapState, action: SelectionMoveUp): void => {
    if (state.currentViewType === ViewType.Tracking) {
        moveTrackingSelectionUp(state);
        return;
    }

    const card = smUse.getActiveCard();
    if (!card) {
        const firstActivity = orderBy(state.activities, 'order').shift();
        firstActivity.isActive = true;
        return;
    }

    const cardType = smUse.getCardType(card);
    switch (cardType) {
        case smUse.CardType.Activity:
            break;
        case smUse.CardType.Task:
            moveTaskSelection(card as Task, state);
            break;
        case smUse.CardType.Subtask:
            moveSubtaskSelection(card as Subtask, state);
            break;
    }
};

const moveTrackingSelectionUp = (state: StorymapState) => {
    const releaseId = state.trackingViewState.releaseId;
    if (!releaseId) {
        return;
    }

    const currentCard = smUse.getActiveCard();
    const firstCard = getFirstTrackingVisibleCard(state, releaseId);

    if (!firstCard) {
        return;
    }

    if (!smUse.isSubtask(currentCard) || !getTrackingVisibleCardPosition(state, currentCard.id, releaseId)) {
        firstCard.isActive = true;
        currentCard && (currentCard.isActive = false);
        return;
    }

    const currentPosition = getTrackingVisibleCardPosition(state, currentCard.id, releaseId);
    if (!currentPosition || currentPosition.cardIndex === 0) {
        return;
    }

    const columns = getTrackingVisibleStatusColumns(state, releaseId);
    const nextCard = columns[currentPosition.columnIndex].cards[currentPosition.cardIndex - 1];
    if (!nextCard) {
        return;
    }

    nextCard.isActive = true;
    currentCard.isActive = false;
};

const moveTaskSelection = (current: Task, state: StorymapState) => {
    const next = state.activities.find((x) => x.id == current.activityId);
    if (next) {
        next.isActive = true;
        current.isActive = false;
    }
};

const moveSubtaskSelection = (current: Subtask, state: StorymapState) => {
    const currentRelease = state.releases.find((x) => x.id == current.releaseId);
    const pinnedRelease = state.releases.find((x) => x.isPinned);

    // Filter subtasks by hide unfiltered mode if active
    let baseSubtasks = state.subtasks;
    if (isHideUnfilteredActive.value) {
        baseSubtasks = state.subtasks.filter((x) => matchingSubtaskIdSet.value.has(x.id));
    }

    let mappedSubtasks = baseSubtasks
        .filter((x) => x.taskId == current.taskId)
        .filter((x) =>
            pinnedRelease == null
                ? true
                : currentRelease.id === pinnedRelease.id
                    ? x.releaseId === pinnedRelease.id
                    : x.releaseId !== pinnedRelease.id
        )
        .map((x) => {
            const release = state.releases.find((r) => r.id == x.releaseId);
            return {
                releaseId: release.id,
                releaseOrder: release.order,
                releaseIsArchived: release.isArchived,
                releaseIsCollapsed: release.isCollapsed,
                isReleaseFocused: release.isFocused,
                order: x.order,
                subtask: x,
            };
        });

    mappedSubtasks = mappedSubtasks.filter((x) => {
        if (x.releaseIsArchived) {
            return false;
        }

        if (x.releaseIsCollapsed) {
            return false;
        }

        if (hasFocusedRelease.value && !x.isReleaseFocused) {
            return false;
        }

        if (x.releaseId == current.releaseId) {
            return x.order < current.order;
        }

        return x.releaseOrder < currentRelease.order;
    });

    const next = orderBy(mappedSubtasks, ['releaseOrder', 'order'], ['desc', 'desc']).shift();
    if (next) {
        next.subtask.isActive = true;
        current.isActive = false;
    } else {
        if (currentRelease.isPinned) {
            return;
        }

        const task = state.tasks.find((x) => x.id == current.taskId);
        // Only select task if it's visible (respects hide unfiltered mode)
        if (task && (!isHideUnfilteredActive.value || visibleTaskIdSet.value.has(task.id))) {
            task.isActive = true;
            current.isActive = false;
        }
    }
};

//@ts-ignore
registerReducer(SelectionMoveUp.ACTION_TYPE, reducer);
